/*
 * Hover-card fields (issue 376) — the reader's own choice of which fields a
 * family's hover card shows, per game system.
 *
 * STORAGE IS THE PREFERENCES BAG, NOT A STORE OF ITS OWN: the map lives at
 * `hoverFields[<system-slug>][<family key>]` inside the one synced preferences
 * row, mirrored locally under the localStorage key `hoverFields` exactly as
 * allowedSources mirrors its map — `preferencesSync.collectLocal` picks it up,
 * `applyBag` writes it back. This module is the read/write seam the card and
 * the profile tab use.
 *
 * WHAT MAY BE PLACED ON A CARD is the registry's answer, not a list: a
 * family's VALUE attributes minus prose and structured datatypes — numbers,
 * short text, booleans — the rule the suggestion preview already applies. A
 * reference is not offered: the previews endpoint ships an entry's scalars
 * only (its links live in another table), so a chosen reference would be a
 * row that never fills.
 *
 * An unconfigured family falls back to the #73 defaults (`detail.quickStats`):
 * configuration is an override, never a requirement.
 */
import { cleanHoverFieldList, cleanHoverFieldsMap, pushNow, MAX_HOVER_FIELDS } from './preferencesSync';

/** How many of the registry defaults a card shows when the reader chose
 *  nothing (#73's cap). The tab starts from exactly these, so what it lists
 *  is what the card draws — and a trim of it stays under the choice cap. */
export const DEFAULT_CARD_ROWS = 6;

/** Fired on window whenever this browser changes a choice, so mounted UI can re-read. */
export const HOVER_FIELDS_CHANGED_EVENT = 'tdt:hover-fields-changed';

const KEY = 'hoverFields';
const NOT_COMPACT = new Set(['richtext', 'structured', 'table']);

function readMap() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return cleanHoverFieldsMap(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

/** Is this attribute one a card may show — a compact scalar value? */
export function isCompact(attr) {
  return !!attr && attr.kind === 'value' && !attr.isProse && !NOT_COMPACT.has(attr.datatype)
    && attr.register !== 'name';
}

/** The fields a family offers for a card: its compact values, in declaration order. */
export function offeredFields(family) {
  return (family && family.attributes ? family.attributes : []).filter(isCompact);
}

/** The reader's ordered choice for one family, or null when they made none. */
export function getHoverFields(systemSlug, familyKey) {
  const map = readMap();
  if (!map || !systemSlug || !familyKey || !map[systemSlug]) return null;
  return cleanHoverFieldList(map[systemSlug][familyKey]);
}

/**
 * Save (or clear, with null/[]) the choice for one family, then push the
 * whole preferences snapshot up. Returns pushNow()'s promise (false when the
 * push could not land — the local mirror still holds).
 */
export function setHoverFields(systemSlug, familyKey, keys) {
  if (!systemSlug || !familyKey) return Promise.resolve(false);
  const map = readMap() || {};
  const clean = cleanHoverFieldList(keys);
  const families = { ...(map[systemSlug] || {}) };
  if (clean) families[familyKey] = clean; else delete families[familyKey];
  if (Object.keys(families).length) map[systemSlug] = families; else delete map[systemSlug];
  try {
    if (Object.keys(map).length) localStorage.setItem(KEY, JSON.stringify(map));
    else localStorage.removeItem(KEY);
  } catch (e) {
    /* private mode etc. — the push below still carries the intent */
  }
  try {
    window.dispatchEvent(new Event(HOVER_FIELDS_CHANGED_EVENT));
  } catch (e) {
    /* non-browser environment */
  }
  return pushNow();
}

export { MAX_HOVER_FIELDS };
